import Logger from 'lib/utils/Logger';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import URLON from 'urlon';

const log = new Logger('useQueryParam');

export function toLink(path, params = {}) {
	return `${path}?${URLON.stringify(params)}`;
}

const NO_PARAMS: Params = {};

// biome-ignore lint/suspicious/noExplicitAny: ignore
export type Params = Partial<Record<string, any>>;
type SetParamOptions = { replace?: boolean; shallow?: boolean };
type UseQueryParams<T> = [T, (params: T, options?: SetParamOptions) => void];

/**
 * React hook for getting and setting URL query params as JSON
 *
 * This uses the URL search string, independent of the navigation path used by react router,
 * and is very useful for various state like filters.
 *
 * The search string is serialized by URLON. It encodes and decodes like JSON,
 * but unlike JSON it doesn't break links. Typically brackets and curly braces
 * breaks link parsing in various software like email and chat. By using URLON,
 * the link parsing stays intact and the linked can be clicked.
 *
 * Also, like JSON, the main advantage is that we can still serialize nested  data.
 */
export default function useQueryParams<T = Params>(): UseQueryParams<T> {
	const router = useRouter();
	const search = window.location.search.substr(1);

	const queryParams: T = useMemo(() => {
		try {
			return search ? URLON.parse(search) : NO_PARAMS;
		} catch (e) {
			console.error(e);
			log.warn('URLON: failed to parse params from location.search', { search });
			return NO_PARAMS;
		}
	}, [search]);

	const setParams: (params: T, options?: SetParamOptions) => void = useCallback(
		(params, options = {}) => {
			if (isEqual(queryParams, params)) {
				return;
			}
			const newParams = { ...queryParams, ...params };
			const newSearch = `?${URLON.stringify(newParams)}`;
			router.push({ search: newSearch }, undefined, options);
		},
		[queryParams, router]
	);

	return [queryParams, setParams];
}

// Open path + searchParams in a new browser tab
export const openInNewTab = (path: string, searchParams = {}): void => {
	if (path?.endsWith('/')) {
		path = path.substring(0, path.length - 1);
	}
	const newWindow = window.open(toLink(path, searchParams), '_blank', 'noopener,noreferrer');
	if (newWindow) {
		newWindow.opener = null;
	}
};
